import type { FC, HTMLAttributes, PropsWithChildren, ReactNode } from "react";
import { Children, isValidElement } from "react";

import styled from "styled-components";

import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const TopRightComponentContainer = styled(FlexRow)`
    justify-content: flex-end;
    flex: 1;
`;

const SubNavPageTitle = styled.div<{ shouldExpand: boolean }>`
    flex-grow: ${(props) => (props.shouldExpand ? 1 : 0)};
    text-overflow: ellipsis;
    overflow: hidden;
`;

interface INavigationMatrixProps {
    bottomRightComponent?: ReactNode;
    topLeftComponent?: ReactNode;
    topRightComponent?: ReactNode;
    bottomLeftComponent?: ReactNode;
    education?: ReactNode;
    onTopComponentMouseEnter?: () => void;
    onTopComponentMouseLeave?: () => void;
}

const BottomBarRightComponent: FC<PropsWithChildren> = ({ children }) => {
    if (!children) {
        return null;
    }

    const childrenArray = Children.toArray(children);
    if (childrenArray.length === 0 || !childrenArray.some(isValidElement)) {
        return null;
    }

    return (
        <div
            data-automation="navigation-matrix-bottom-right"
            className="subNav-filters"
        >
            {childrenArray}
        </div>
    );
};

const BottomBarLeftComponent: FC<PropsWithChildren> = ({ children }) => {
    if (!children) {
        return null;
    }

    const childrenArray = Children.toArray(children);
    if (childrenArray.length === 0 || !childrenArray.some(isValidElement)) {
        return null;
    }

    return (
        <SubNavPageTitle
            data-automation="navigation-matrix-bottom-left"
            shouldExpand={Boolean(children)}
        >
            {childrenArray}
        </SubNavPageTitle>
    );
};

const BottomBarWrapper: FC<{
    bottomLeftComponent?: ReactNode;
    bottomRightComponent?: ReactNode;
}> = ({ bottomLeftComponent, bottomRightComponent }) => {
    const hasBottomLeft = bottomLeftComponent && Children.toArray(bottomLeftComponent).some(isValidElement);
    const hasBottomRight = bottomRightComponent && Children.toArray(bottomRightComponent).some(isValidElement);

    if (!hasBottomLeft && !hasBottomRight) {
        return null;
    }

    return (
        <div
            data-automation="navigation-matrix-bottom-bar"
            className="subNav u-flex-row u-flex-center u-flex-space-between subNav-secondRow"
        >
            <BottomBarLeftComponent>{bottomLeftComponent}</BottomBarLeftComponent>
            <BottomBarRightComponent>{bottomRightComponent}</BottomBarRightComponent>
        </div>
    );
};

const TopLeftComponent: FC<PropsWithChildren> = ({ children }) => {
    if (!children) {
        return null;
    }

    return (
        <div
            className="subNav-header"
            data-automation="navigation-matrix-top-left"
        >
            {children}
        </div>
    );
};

const TopRightComponent: FC<PropsWithChildren> = ({ children }) => {
    if (!children) {
        return null;
    }

    return (
        <TopRightComponentContainer data-automation="navigation-matrix-top-right">
            {children}
        </TopRightComponentContainer>
    );
};

const EducationComponentWrapper: FC<PropsWithChildren> = ({ children }) => {
    if (!children) {
        return null;
    }

    return (
        <div
            data-automation="navigation-matrix-education"
            className="subNav-education"
        >
            {children}
        </div>
    );
};

const TopBarWrapper: FC<
    HTMLAttributes<HTMLDivElement> & {
        topLeftComponent?: ReactNode;
        topRightComponent?: ReactNode;
    }
> = ({ topLeftComponent, topRightComponent, ...rest }) => {
    if (!topLeftComponent && !topRightComponent) {
        return null;
    }
    return (
        <div
            data-automation="navigation-matrix-top-bar"
            className="subNav u-flex-row u-flex-center"
            {...rest}
        >
            <TopLeftComponent>{topLeftComponent}</TopLeftComponent>
            <TopRightComponent>{topRightComponent}</TopRightComponent>
        </div>
    );
};

const PageHeaderWrapper: FC<PropsWithChildren> = ({ children }) => {
    return <div data-automation="navigation-matrix-wrapper">{children}</div>;
};

export const PageHeader: FC<INavigationMatrixProps> = (props) => {
    const {
        topLeftComponent,
        topRightComponent,
        bottomRightComponent,
        education,
        bottomLeftComponent,
        onTopComponentMouseEnter,
        onTopComponentMouseLeave,
    } = props;

    return (
        <PageHeaderWrapper>
            <TopBarWrapper
                onMouseEnter={onTopComponentMouseEnter}
                onMouseLeave={onTopComponentMouseLeave}
                topLeftComponent={topLeftComponent}
                topRightComponent={topRightComponent}
            />
            <BottomBarWrapper
                bottomLeftComponent={bottomLeftComponent}
                bottomRightComponent={bottomRightComponent}
            />
            <EducationComponentWrapper>{education}</EducationComponentWrapper>
        </PageHeaderWrapper>
    );
};
